import React, { useEffect } from 'react';
import { createContext, useState } from 'react';
import { Row } from '../../component/types';
import {
  SelectedColumnData,
  SelectedIdsData,
  TableContextType,
  TableProviderProps,
} from './provider-types';

export const TableContext = createContext<TableContextType | null>(null);

function TableProvider<T extends Row>({
  children,
  check,
  rows,
  columns,
  showExtendedDeleteView,
  activePage,
}: TableProviderProps<T>) {
  const [selectedIdsData, setSelectedIdsData] = useState<SelectedIdsData>({
    selectedIds: [],
  });
  const [showExtendedDelete, setShowExtendedDelete] = useState(false);
  const [deleteBtnText, setDeleteBtnText] = useState('');

  const [selectedColumnIdsData, setSelectedColumnIdsData] =
    useState<SelectedColumnData>(
      check?.defaultCheckedColumnData ? check?.defaultCheckedColumnData : {},
    );

  useEffect(() => {
    if (check?.defaultCheckedColumnData) {
      setSelectedColumnIdsData(check?.defaultCheckedColumnData);
    }
  }, [check?.defaultCheckedColumnData]);

  const onCheckButtonChange = (selectedIds: (number | string)[]) => {
    if (check) {
      switch (true) {
        case selectedIds.length < rows.length && selectedIds.length > 1:
          setDeleteBtnText('Delete Selected');
          break;
        case selectedIds.length === rows.length:
          setDeleteBtnText('Delete All');
          break;
        case selectedIds.length === 1:
          setDeleteBtnText('Delete');
          break;
      }
      check?.onCheck && check?.onCheck(selectedIds);
    }
  };

  const onCheckedAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (showExtendedDeleteView?.showExtendedDelete && !showExtendedDelete) {
      setShowExtendedDelete(true);
    }
    if (e.target.checked) {
      const allId = [];
      rows.forEach((element) => allId.push(element?.id));

      setSelectedIdsData((state) => {
        !check?.isHideSelectionView && onCheckButtonChange(allId);
        return {
          ...state,
          selectedIds: Array.from(new Set([...state.selectedIds, ...allId])),
          [activePage]: { active: true },
        };
      });

      // check row
      if (check?.isCheckRow) {
        const checkAllData = {};
        columns.map((column) => {
          if (column?.isCheckAble) {
            checkAllData[column.id.toString()] = {
              rowIds: allId,
              activePage: activePage,
            };
          }
        });

        setSelectedColumnIdsData(checkAllData);
        check?.onCheckColumn && check?.onCheckColumn(checkAllData);
      }
    } else {
      const allId = [];
      rows.forEach((element) => {
        allId.push(element?.id);
      });
      setSelectedIdsData((state) => {
        const selectedIds = state.selectedIds.filter(
          (item) => !allId.includes(item),
        );
        return { ...state, selectedIds, [activePage]: { active: false } };
      });

      if (!check?.isHideSelectionView) {
        onCheckButtonChange([]);
        if (showExtendedDeleteView?.showExtendedDelete) {
          setShowExtendedDelete(false);
        }
      }

      // uncheck row
      if (check.isCheckRow) {
        const checkAllData = {};
        columns.map((column) => {
          if (column?.isCheckAble) {
            checkAllData[column.id.toString()] = {
              rowIds: [],
              activePage: activePage,
            };
          }
        });
        setSelectedColumnIdsData(checkAllData);
        check?.onCheckColumn && check?.onCheckColumn(checkAllData);
      }
    }
  };

  const onClickCheckbox = (id: number | string) => {
    if (showExtendedDeleteView?.showExtendedDelete && !showExtendedDelete) {
      setShowExtendedDelete(true);
    }

    const newValue = selectedIdsData.selectedIds;
    if (selectedIdsData.selectedIds.includes(id as never)) {
      const value = newValue.filter((item) => item !== id);
      setSelectedIdsData((state) => {
        return { ...state, selectedIds: value };
      });
      !check?.isHideSelectionView && onCheckButtonChange(value);

      // uncheck row
      if (check?.isCheckRow) {
        columns.map((column) => {
          if (column?.isCheckAble) {
            const rowIds = selectedColumnIdsData?.[
              column.id.toString()
            ]?.rowIds?.filter((rowid) => rowid !== id);

            setSelectedColumnIdsData((state) => {
              const newState = { ...state };
              newState[column.id.toString()].rowIds = rowIds;
              newState[column.id.toString()].activePage = activePage;

              check?.onCheckColumn && check?.onCheckColumn(newState);
              return newState;
            });
          }
        });
      }
    } else {
      let value: (number | string)[];
      setSelectedIdsData((state) => {
        value = [...state.selectedIds, id];
        !check?.isHideSelectionView && onCheckButtonChange(value);
        return { ...state, selectedIds: value };
      });
      // check row
      if (check?.isCheckRow) {
        columns.map((column) => {
          if (column?.isCheckAble) {
            if (selectedColumnIdsData?.[column.id.toString()]) {
              const isExists =
                selectedColumnIdsData?.[column.id.toString()]?.rowIds?.includes(
                  id,
                );

              if (!isExists) {
                const rowIds =
                  selectedColumnIdsData?.[column.id.toString()].rowIds;

                rowIds.push(id.toString());
                setSelectedColumnIdsData((state) => {
                  const newState = { ...state };
                  newState[column.id.toString()].rowIds = rowIds;
                  newState[column.id.toString()].activePage = activePage;
                  check?.onCheckColumn && check?.onCheckColumn(newState);
                  return newState;
                });
              }
            } else {
              setSelectedColumnIdsData((state) => {
                const newState = {
                  ...state,
                  [column.id.toString()]: {
                    rowIds: [id],
                    activePage: activePage,
                  },
                };
                check?.onCheckColumn && check?.onCheckColumn(newState);
                return newState;
              });
            }
          }
        });
      }
    }
  };

  // Function to handle all column check
  const onColumnCheck = (columnId: string | number) => {
    const allId = [];
    rows.forEach((element) => {
      allId.push(element?.id.toString());
    });

    if (
      selectedColumnIdsData[columnId] &&
      selectedColumnIdsData[columnId]?.rowIds.length > 0
    ) {
      setSelectedColumnIdsData((state) => {
        const newState = {
          ...state,
          [columnId.toString()]: { rowIds: [], activePage: activePage },
        };
        check?.onCheckColumn && check?.onCheckColumn(newState);
        return newState;
      });
    } else {
      setSelectedColumnIdsData((state) => {
        const newState = {
          ...state,
          [columnId.toString()]: { rowIds: allId, activePage: activePage },
        };
        check?.onCheckColumn && check?.onCheckColumn(newState);
        return newState;
      });
    }
  };

  const onClickColumnCheckbox = (columnId: string, rowId: string) => {
    if (selectedColumnIdsData[columnId.toString()]) {
      if (selectedColumnIdsData[columnId.toString()]?.rowIds?.includes(rowId)) {
        const value = selectedColumnIdsData[columnId.toString()].rowIds.filter(
          (item) => item.toString() !== rowId,
        );
        setSelectedColumnIdsData((state) => {
          const newState = { ...state };
          newState[columnId.toString()].rowIds = value;
          check?.onCheckColumn && check?.onCheckColumn(newState);
          return newState;
        });
      } else {
        setSelectedColumnIdsData((state) => {
          const newState = { ...state };
          newState[columnId.toString()].rowIds.push(rowId);
          check?.onCheckColumn && check?.onCheckColumn(newState);
          return newState;
        });
      }
    } else {
      setSelectedColumnIdsData((state) => {
        const newState = {
          ...state,
          [columnId.toString()]: {
            rowIds: [rowId],
            activePage: activePage,
          },
        };
        check?.onCheck && check?.onCheckColumn(newState);
        return newState;
      });
    }
  };

  return (
    <TableContext.Provider
      value={{
        selectedIdsData,
        setSelectedIdsData,
        onCheckedAll,
        onClickCheckbox,

        deleteBtnText,
        onCheckButtonChange,

        showExtendedDelete,
        setShowExtendedDelete,

        selectedColumnIdsData,
        onColumnCheck,
        onClickColumnCheckbox,
      }}
    >
      {children}
    </TableContext.Provider>
  );
}

export default TableProvider;
